import { useRef } from 'react';
import { Link, useLocation } from '@tanstack/react-router';

import { useActiveIndicator } from '../../lib/motion/hooks';

import { pendingCount, useAgentsBoard } from '../../features/agents/useAgentsBoard';
import { IntelligenceLauncher } from '../../features/intelligence/IntelligenceLauncher';
import { folderLock, NavLabel } from './NavLabel';
import { folderForPath, type Folder, type NavModel } from './navModel';

/** What waits for review on Dale Agents; only asked for when the folder exists. */
function AgentsCount() {
  const pending = pendingCount(useAgentsBoard().data);
  if (pending <= 0) return null;
  return (
    <em className="hs-count">
      {pending}
      <span className="sr-only"> por revisar</span>
    </em>
  );
}

/** Only an open Agentes asks the board for its count: a locked one would only earn a refused request. */
function holdsAgents(folder: Folder): boolean {
  return folder.links.some((link) => link.to === '/agentes' && !link.lock);
}

function FolderTab({ folder, active }: { folder: Folder; active: boolean }) {
  const first = folder.links[0]!;
  return (
    <span className="hs-tab-wrap" data-active={active || undefined}>
      <Link to={first.to} className="hs-tab" aria-current={active ? 'page' : undefined}>
        <NavLabel label={folder.label} lock={folderLock(folder.links)} />
        {holdsAgents(folder) ? <AgentsCount /> : null}
      </Link>
    </span>
  );
}

/**
 * The folder tabs of the Helios CRM: the open one is lit from behind and grows
 * into the sheet below; the light travels to the next folder you open. The reports' pocket rides at the end of the row,
 * except inside Dale Agents, which already holds the reports.
 */
export function FolderTabs({ model }: { model: NavModel }) {
  const { pathname } = useLocation();
  const activeId = folderForPath(model, pathname)?.id;
  const tabs = useRef<HTMLElement>(null);
  useActiveIndicator(tabs, '.hs-tab-wrap[data-active]', activeId);
  return (
    <div className="hs-tabs-row">
      <nav className="hs-tabs" aria-label="Carpetas" ref={tabs}>
        <span className="ind hs-tab-glow" aria-hidden="true" />
        {model.folders.map((folder) => (
          <FolderTab key={folder.id} folder={folder} active={folder.id === activeId} />
        ))}
      </nav>
      {activeId === 'equipo' ? null : (
        <div className="hs-pocket">
          <IntelligenceLauncher />
        </div>
      )}
    </div>
  );
}
